import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ProductThumbnail } from "../components/ProductThumbnail";
import { toast } from "sonner";
import axios from "axios";
import { format, startOfMonth, endOfMonth, subMonths, subDays, subYears } from "date-fns";
import { es } from "date-fns/locale";
import { CalendarIcon, TrendingUp, DollarSign, Package, ShoppingBag, Store, Users, Boxes } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";
import { BranchSelector, BranchBadge } from "../components/BranchSelector";

const MerchDashboardPage = () => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [dateRange, setDateRange] = useState("month");
  const [startDate, setStartDate] = useState(startOfMonth(new Date()));
  const [endDate, setEndDate] = useState(endOfMonth(new Date()));
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [branchId, setBranchId] = useState("");
  const [branchObj, setBranchObj] = useState(null);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount || 0);
  };

  useEffect(() => {
    fetchDashboardData();
  }, [startDate, endDate, branchId]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const formattedStartDate = format(startDate, "yyyy-MM-dd");
      const formattedEndDate = format(endDate, "yyyy-MM-dd");
      const params = {
        start_date: formattedStartDate,
        end_date: formattedEndDate,
      };
      if (branchId) params.branch_id = branchId;

      const response = await axios.get(`${API}/merch/dashboard`, {
        params,
        ...getAuthHeaders()
      });
      setSummary(response.data);
    } catch (error) {
      console.error("Dashboard error:", error.response?.data || error.message);
      toast.error("Error al cargar datos del dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (value) => {
    setDateRange(value);
    setShowCustomRange(value === "custom");
    const now = new Date();
    switch (value) {
      case "today":
        setStartDate(now);
        setEndDate(now);
        break;
      case "yesterday":
        const yesterday = subDays(now, 1);
        setStartDate(yesterday);
        setEndDate(yesterday);
        break;
      case "day_before":
        const dayBefore = subDays(now, 2);
        setStartDate(dayBefore);
        setEndDate(dayBefore);
        break;
      case "month":
        setStartDate(startOfMonth(now));
        setEndDate(endOfMonth(now));
        break;
      case "last_month":
        const lastMonth = subMonths(now, 1);
        setStartDate(startOfMonth(lastMonth));
        setEndDate(endOfMonth(lastMonth));
        break;
      case "quarter":
        setStartDate(subMonths(startOfMonth(now), 2));
        setEndDate(endOfMonth(now));
        break;
      case "six_months":
        setStartDate(subMonths(startOfMonth(now), 5));
        setEndDate(endOfMonth(now));
        break;
      case "year":
        setStartDate(new Date(now.getFullYear(), 0, 1));
        setEndDate(new Date(now.getFullYear(), 11, 31));
        break;
      case "last_year":
        const lastYear = subYears(now, 1);
        setStartDate(new Date(lastYear.getFullYear(), 0, 1));
        setEndDate(new Date(lastYear.getFullYear(), 11, 31));
        break;
      case "custom":
        // Don't change dates, let user pick
        break;
      default:
        break;
    }
  };

  const channelData = [
    { name: 'Tienda Física', value: summary?.odoo_gross || 0, color: '#8B5CF6' },
    { name: 'Darkar.co', value: summary?.shopify_gross || 0, color: '#10B981' }
  ];

  if (loading && !summary) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="spinner w-8 h-8 border-4 border-[#F59E0B] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="merch-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold text-[#4A4A4A] font-['Manrope']">Dashboard Merch</h1>
              <BranchBadge branch={branchObj} />
            </div>
            <p className="text-[#6B7280]">
              Resumen de ventas y costos de mercancía · <span className="text-[#8B5CF6]">Fuente: Shopify + Odoo Merch</span>
            </p>
            {/* SKU Tracking Indicator */}
            {summary && (summary.merch_internal_products_count > 0 || summary.tracked_skus_count > 0) && (
              <div className="flex items-center gap-2 mt-2">
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-[#F59E0B]/10 rounded-full border border-[#F59E0B]/30">
                  <div className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse"></div>
                  <span className="text-xs font-medium text-[#92400E]">
                    Trackeando {summary.tracked_skus_count || 0} SKUs de {summary.merch_internal_products_count || 0} producto{summary.merch_internal_products_count !== 1 ? 's' : ''} interno{summary.merch_internal_products_count !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            )}
            {summary && summary.merch_internal_products_count === 0 && (
              <div className="flex items-center gap-2 mt-2">
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 rounded-full border border-amber-200">
                  <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                  <span className="text-xs font-medium text-amber-700">
                    Sin productos marcados como Merch. Ve a Productos y activa el toggle "Producto de Merch".
                  </span>
                </div>
              </div>
            )}
          </div>
          
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <BranchSelector value={branchId} onChange={(id, obj) => { setBranchId(id); setBranchObj(obj); }} />
            <Select value={dateRange} onValueChange={handleDateRangeChange}>
              <SelectTrigger className="w-[180px] bg-white border-[#FECDD3]" data-testid="date-range-select">
                <CalendarIcon className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Período" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="today">Hoy</SelectItem>
                <SelectItem value="yesterday">Ayer</SelectItem>
                <SelectItem value="day_before">Antier</SelectItem>
                <SelectItem value="month">Este Mes</SelectItem>
                <SelectItem value="last_month">Mes Anterior</SelectItem>
                <SelectItem value="quarter">Últimos 3 Meses</SelectItem>
                <SelectItem value="six_months">Últimos 6 Meses</SelectItem>
                <SelectItem value="year">Este Año</SelectItem>
                <SelectItem value="last_year">Año Pasado</SelectItem>
                <SelectItem value="custom">Rango Personalizado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Custom Date Range Pickers */}
          {showCustomRange && (
            <div className="flex items-center gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="bg-white border-[#FECDD3] text-sm">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {format(startDate, "dd/MM/yyyy", { locale: es })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-white" align="start">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={(date) => date && setStartDate(date)}
                    locale={es}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <span className="text-[#6B7280]">a</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="bg-white border-[#FECDD3] text-sm">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {format(endDate, "dd/MM/yyyy", { locale: es })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-white" align="start">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={(date) => date && setEndDate(date)}
                    locale={es}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          )}
        </div>

        {/* Main Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Total Bruto */}
          <Card className="bg-white border-[#F59E0B]/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[#6B7280]">Venta Bruta Total</p>
                  <p className="text-xl font-bold text-[#F59E0B]">{formatCurrency(summary?.total_gross)}</p>
                  <div className="flex flex-col gap-0.5 mt-1 text-[10px]">
                    <span className="text-[#8B5CF6]">Física: {formatCurrency(summary?.odoo_gross)}</span>
                    <span className="text-[#10B981]">Online: {formatCurrency(summary?.shopify_gross)}</span>
                  </div>
                </div>
                <ShoppingBag className="w-8 h-8 text-[#F59E0B]/20" />
              </div>
            </CardContent>
          </Card>

          {/* Costo Total */}
          <Card className="bg-white border-[#EF4444]/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[#6B7280]">Costo Total</p>
                  <p className="text-xl font-bold text-[#EF4444]">{formatCurrency(summary?.total_cost)}</p>
                  <div className="flex flex-col gap-0.5 mt-1 text-[10px]">
                    <span className="text-[#8B5CF6]">Física: {formatCurrency(summary?.odoo_cost)}</span>
                    <span className="text-[#10B981]">Online: {formatCurrency(summary?.shopify_cost)}</span>
                  </div>
                </div>
                <DollarSign className="w-8 h-8 text-[#EF4444]/20" />
              </div>
            </CardContent>
          </Card>

          {/* Margen Heladería - NEW */}
          <Card className="bg-white border-[#3B82F6]/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[#6B7280]">Margen Heladería</p>
                  <p className={`text-xl font-bold ${(summary?.heladeria_margin || 0) < 0 ? 'text-[#EF4444]' : 'text-[#3B82F6]'}`}>
                    {formatCurrency(summary?.heladeria_margin)}
                  </p>
                  <p className="text-[10px] text-[#6B7280] mt-1">% asignado a Heladería</p>
                </div>
                <Store className="w-8 h-8 text-[#3B82F6]/20" />
              </div>
            </CardContent>
          </Card>

          {/* Margen Merch (was Margen Darkar) */}
          <Card className="bg-white border-[#10B981]/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[#6B7280]">Margen Merch</p>
                  <p className={`text-xl font-bold ${(summary?.merch_margin || 0) < 0 ? 'text-[#EF4444]' : 'text-[#10B981]'}`}>
                    {formatCurrency(summary?.merch_margin)}
                  </p>
                  <p className="text-[10px] text-[#6B7280] mt-1">% asignado a Merch</p>
                </div>
                <TrendingUp className="w-8 h-8 text-[#10B981]/20" />
              </div>
            </CardContent>
          </Card>

          {/* Margen Proveedores */}
          <Card className="bg-white border-[#8B5CF6]/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[#6B7280]">Margen Proveedores</p>
                  <p className="text-xl font-bold text-[#8B5CF6]">{formatCurrency(summary?.proveedor_margin)}</p>
                  <p className="text-[10px] text-[#6B7280] mt-1">Productos con toggle Proveedor</p>
                </div>
                <Users className="w-8 h-8 text-[#8B5CF6]/20" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Investment Recovery */}
        <Card className="bg-gradient-to-r from-[#F59E0B]/10 to-[#EF4444]/10 border-[#F59E0B]/30">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#F59E0B]/20 flex items-center justify-center">
                  <Boxes className="w-6 h-6 text-[#F59E0B]" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#4A4A4A]">Inversión en Lotes</h3>
                  <p className="text-sm text-[#6B7280]">Seguimiento de inversión y recuperación</p>
                </div>
              </div>
              <div className="flex gap-6">
                <div className="text-center">
                  <p className="text-2xl font-bold text-[#F59E0B]">{formatCurrency(summary?.batches_summary?.total_invested)}</p>
                  <p className="text-xs text-[#6B7280]">Invertido</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-[#10B981]">{formatCurrency(summary?.batches_summary?.total_recovered)}</p>
                  <p className="text-xs text-[#6B7280]">Recuperado</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-[#EF4444]">{formatCurrency(summary?.batches_summary?.pending_recovery)}</p>
                  <p className="text-xs text-[#6B7280]">Pendiente</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Charts Row - Only Sales by Channel */}
        <Card className="bg-white border-[#FECDD3]">
          <CardHeader>
            <CardTitle className="text-[#4A4A4A] font-['Manrope']">Ventas por Canal</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={channelData}>
                  <XAxis dataKey="name" tick={{ fill: '#6B7280', fontSize: 12 }} />
                  <YAxis tick={{ fill: '#6B7280', fontSize: 12 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Bar dataKey="value" fill="#F59E0B" radius={[4, 4, 0, 0]}>
                    {channelData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-6 mt-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#8B5CF6]"></div>
                <span className="text-sm text-[#6B7280]">Tienda Física ({summary?.odoo_sales_count || 0})</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#10B981]"></div>
                <span className="text-sm text-[#6B7280]">Darkar.co ({summary?.shopify_sales_count || 0})</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Costs by Internal Product */}
        <Card className="bg-white border-[#FECDD3]">
          <CardHeader>
            <CardTitle className="text-[#4A4A4A] font-['Manrope']">Costos por Producto</CardTitle>
          </CardHeader>
          <CardContent>
            {summary?.sales_by_product?.length > 0 ? (
              <div className="flex items-center gap-4">
                <div className="h-72 w-1/2">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={summary.sales_by_product.map((p, i) => ({
                          name: p.product_name,
                          value: p.cost_total || 0,
                          color: ['#F43F5E', '#10B981', '#F59E0B', '#3B82F6', '#8B5CF6', '#EC4899', '#14B8A6'][i % 7]
                        }))}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={90}
                        innerRadius={40}
                      >
                        {summary.sales_by_product.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={['#F43F5E', '#10B981', '#F59E0B', '#3B82F6', '#8B5CF6', '#EC4899', '#14B8A6'][index % 7]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="w-1/2 space-y-2 max-h-72 overflow-y-auto pr-2">
                  {[...summary.sales_by_product]
                    .sort((a, b) => (b.cost_total || 0) - (a.cost_total || 0))
                    .map((item, index) => (
                    <div key={index} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full flex-shrink-0" 
                          style={{ backgroundColor: ['#F43F5E', '#10B981', '#F59E0B', '#3B82F6', '#8B5CF6', '#EC4899', '#14B8A6'][index % 7] }}
                        />
                        <span className="text-[#6B7280] truncate">{item.product_name}</span>
                      </div>
                      <span className="text-[#EF4444] font-mono ml-2">{formatCurrency(item.cost_total)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-72 flex items-center justify-center text-[#9CA3AF]">
                No hay datos de costos para este período
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Products Table */}
        <Card className="bg-white border-[#FECDD3]">
          <CardHeader>
            <CardTitle className="text-[#4A4A4A] font-['Manrope']">Productos Más Vendidos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#E5E7EB]">
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#6B7280]">Producto</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-[#6B7280]">Unidades</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-[#6B7280]">Venta Bruta</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-[#6B7280]">Costo</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-[#6B7280]">Margen</th>
                  </tr>
                </thead>
                <tbody>
                  {summary?.sales_by_product?.slice(0, 10).map((product, index) => {
                    // Margen total = heladeria + merch + proveedor (suma de todos los márgenes)
                    const totalMargin = (product.heladeria_margin || 0) + (product.merch_margin || 0) + (product.proveedor_margin || 0);
                    return (
                      <tr key={index} className="border-b border-[#E5E7EB] hover:bg-[#FEE2E2]/30">
                        <td className="py-3 px-4 text-[#4A4A4A] font-medium">
                          <div className="flex items-center gap-2">
                            <ProductThumbnail image_url={product.image_url} name={product.product_name} size="sm" />
                            <span className="truncate">{product.product_name}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right text-[#6B7280]">{product.units_sold}</td>
                        <td className="py-3 px-4 text-right text-[#F59E0B] font-mono">{formatCurrency(product.gross_total)}</td>
                        <td className="py-3 px-4 text-right text-[#EF4444] font-mono">{formatCurrency(product.cost_total)}</td>
                        <td className={`py-3 px-4 text-right font-mono ${totalMargin >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                          {formatCurrency(totalMargin)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {(!summary?.sales_by_product || summary.sales_by_product.length === 0) && (
                <div className="text-center py-8 text-[#6B7280]">
                  No hay ventas en este período
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default MerchDashboardPage;
